import * as React from 'react'

import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useF25PricingContext } from 'modules/onboarding/components/Pricing/F25PricingContext'
import { PRO_PLAN_TYPE } from 'modules/onboarding/lib/pricing/plans/utils'

import ContactSales from 'modules/billing/components/FactorialF25Bundles/_ContactSales'
import * as styles from './index.vanilla.css'

type FeatureWithTooltipProps = {
  title: string
  tooltip: string
  isSelected: boolean
}

const FeatureWithTooltip = ({ title, tooltip, isSelected }: FeatureWithTooltipProps) => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt
      label={tooltip}
      attachment="bottom left"
      targetAttachment="top center"
      open={open}
      toggle={toggle}
    >
      <span>
        <Text.Normal
          className={styles.textWithToolTip}
          color={isSelected ? 'grey1000' : 'grey700'}
          weight="medium"
        >
          {title}
        </Text.Normal>
      </span>
    </Tooltip.Alt>
  )
}

type Item = {
  title: string
  description: string
  isSelected: boolean
}

const ItemsColumn = ({ items }: { items?: Item[] }) => {
  return (
    <Box gap="s8" style={{ flex: '1' }}>
      {items?.map((plan) => (
        <Box key={plan.title} flexDirection="row" gap="s4" alignItems="center">
          <Box flexNoShrink>
            <Icon.Medium icon="check" color={plan.isSelected ? 'viridian1000' : 'grey000White'} />
          </Box>
          <FeatureWithTooltip
            title={plan.title}
            tooltip={plan.description}
            isSelected={plan.isSelected}
          />
        </Box>
      ))}
    </Box>
  )
}

const IncludedFeatures = () => {
  const { allPlans, countryFloor } = useF25PricingContext()
  const [includedModules, setIncludedModules] = React.useState<Item[]>()
  const selectedPlan = allPlans.f25Plans.find((p) => p.isSelected)
  const planTitle = selectedPlan?.title
  const isProType = selectedPlan?.planType === PRO_PLAN_TYPE

  const getIncludedModules = () => {
    const coreModules = [
      {
        title: `${countryFloor} ${i18n.t('pricing_plans.employees')}`,
        description: i18n.t('pricing_plans.employees_tooltip'),
        isSelected: true
      },
      {
        title: i18n.t('pricing_plans.core_modules.documents.title'),
        description: i18n.t('pricing_plans.core_modules.documents.description'),
        isSelected: true
      },
      {
        title: i18n.t('pricing_plans.core_modules.signature.title'),
        description: i18n.t('pricing_plans.core_modules.signature.description'),
        isSelected: true
      },
      {
        title: i18n.t('pricing_plans.core_modules.analytics.title'),
        description: i18n.t('pricing_plans.core_modules.analytics.description'),
        isSelected: true
      },
      {
        title: i18n.t('pricing_plans.core_modules.payroll.title'),
        description: i18n.t('pricing_plans.core_modules.payroll.description'),
        isSelected: true
      }
    ]

    const starterModules =
      selectedPlan?.includedAddons?.map((mod) => ({
        title: mod.title,
        description: mod.description || '',
        isSelected: true
      })) || []

    const proModules =
      selectedPlan?.includedExtraAddons?.map((mod) => ({
        title: mod.title,
        description: mod.description || '',
        isSelected: isProType
      })) || []

    return [...coreModules, ...starterModules, ...proModules]
  }

  const divider = includedModules ? Math.ceil(includedModules.length / 3) : 0
  const firstColumn = includedModules?.slice(0, divider)
  const secondColumn = includedModules?.slice(divider, 2 * divider)
  const thirdColumn = includedModules?.slice(2 * divider)

  const emitEventHandler = () => {
    track(trackingEvents.onboarding.requestToContact, {
      page_url: window.location.href,
      request_type: 'Contact Sales',
      from: 'Pricing page contact us'
    })
  }

  useUnsafeEffect(() => {
    if (allPlans?.f25Plans) {
      const modules = getIncludedModules()
      setIncludedModules(modules)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPlans?.f25Plans, isProType])

  if (!includedModules) return null

  return (
    <Box gap="s16">
      <Box
        padding="s24"
        justifyContent="spaceBetween"
        borderRadius={{ all: 'abs008' }}
        border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      >
        <Box gap="s12">
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('pricing_plans.included_in', { plan: planTitle })}:
          </Text.Normal>
          <Box flexDirection="row" justifyContent="spaceBetween" flexWrap="wrap" gap="s8">
            <ItemsColumn items={firstColumn} />
            <ItemsColumn items={secondColumn} />
            <ItemsColumn items={thirdColumn} />
          </Box>
        </Box>
      </Box>
      <Box flexDirection="row" gap="s2" alignItems="center">
        <Text size="200" color="grey700">
          {i18n.t('pricing_plans.contact_question')}
        </Text>
        <ContactSales emitEventHandler={emitEventHandler} />
      </Box>
    </Box>
  )
}

export default IncludedFeatures
